import { io, Socket } from 'socket.io-client';
import { getWebSocketUrl } from '../lib/config';

interface ChatMessage {
  id: number;
  username: string;
  message: string;
  timestamp: string;
  is_moderator?: boolean;
}

interface StreamData {
  viewer_count: number;
  status: string;
  title?: string;
}

class WebSocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<Function>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private keepAliveInterval: NodeJS.Timeout | null = null;

  connect(): void {

    if (this.socket?.connected) return;

    this.socket = io(getWebSocketUrl(), {
      transports: ['websocket'],
      autoConnect: true,
      timeout: 10000,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 2000,
    });

    this.socket.on('connect', () => {
      console.log('Connected to WebSocket server');
      this.reconnectAttempts = 0;
      this.emit('connected', true);
      this.startKeepAlive();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Disconnected from WebSocket server:', reason);
      this.emit('connected', false);
      this.stopKeepAlive();
      
      // Handle free tier sleep mode
      if (reason === 'io server disconnect' || reason === 'transport close') {
        console.log('Server may be sleeping (Render free tier). Will retry in 30 seconds...');
        // Longer delay for free tier to avoid excessive reconnection attempts
        setTimeout(() => this.reconnect(), 30000);
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      this.reconnectAttempts++;
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.log('Max reconnection attempts reached. Server may be sleeping.');
        this.emit('connection-failed', 'Server may be sleeping (free tier limitation)');
      }
    });

    // Chat message received
    this.socket.on('chat-message', (message: ChatMessage) => {
      this.emit('chat-message', message);
    });

    // Viewer count update
    this.socket.on('viewer-count-update', (count: number) => {
      this.emit('viewer-count-update', count);
    });

    // Stream status update
    this.socket.on('stream-status-update', (data: StreamData) => {
      this.emit('stream-status-update', data);
    });

    // Stream started
    this.socket.on('stream-started', (streamData: any) => {
      this.emit('stream-started', streamData);
    });

    // Stream ended
    this.socket.on('stream-ended', (streamData: any) => {
      this.emit('stream-ended', streamData);
    });

    // Document collaboration events
    this.socket.on('document-updated', (data: any) => {
      this.emit('document-updated', data);
    });
  }

  private startKeepAlive(): void {
    // Only enable keep-alive if explicitly configured (for paid tiers)
    const enableKeepAlive = import.meta.env.VITE_ENABLE_KEEP_ALIVE === 'true';
    
    if (enableKeepAlive) {
      // Send ping every 30 seconds to prevent service from sleeping
      this.keepAliveInterval = setInterval(() => {
        if (this.socket?.connected) {
          this.socket.emit('ping');
        }
      }, 30000);
      console.log('Keep-alive enabled - server will not sleep');
    } else {
      console.log('Keep-alive disabled - server may sleep after 15 minutes of inactivity');
    }
  }

  private stopKeepAlive(): void {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }

  private reconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      console.log(`Attempting to reconnect... (${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);
      this.disconnect();
      setTimeout(() => this.connect(), 2000);
    }
  }

  disconnect(): void {
    this.stopKeepAlive();
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.listeners.clear();
    this.reconnectAttempts = 0;
  }

  // Join a stream room
  joinStream(streamId: number): void {
    console.log('Attempting to join stream:', streamId, 'Connected:', this.socket?.connected);
    if (this.socket?.connected) {
      console.log('Emitting join-stream event for streamId:', streamId);
      this.socket.emit('join-stream', streamId);
      this.socket.emit('viewer-join', streamId);
    } else {
      console.log('WebSocket not connected. Attempting to connect...');
      this.connect();
      // Retry joining after connection is established
      setTimeout(() => {
        if (this.socket?.connected) {
          console.log('Retrying join-stream after connection for streamId:', streamId);
          this.socket.emit('join-stream', streamId);
          this.socket.emit('viewer-join', streamId);
        }
      }, 1000);
    }
  }

  // Leave a stream room
  leaveStream(streamId: number): void {
    if (this.socket?.connected) {
      this.socket.emit('viewer-leave', streamId);
    }
  }

  // Send chat message
  sendChatMessage(data: {
    streamId: number;
    message: string;
    userId?: number;
    username: string;
    avatar?: string;
  }): void {
    if (this.socket?.connected) {
      this.socket.emit('chat-message', data);
    } else {
      console.log('WebSocket not connected. Message not sent.');
      this.emit('chat-error', 'Connection lost. Please try again.');
    }
  }

  // Event listener management
  on(event: string, callback: Function): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off(event: string, callback: Function): void {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.delete(callback);
      if (listeners.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  private emit(event: string, data: any): void {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.forEach(callback => callback(data));
    }
  }

  // Get connection status
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // Get connection status with details
  getConnectionStatus(): { connected: boolean; message: string } {
    if (this.socket?.connected) {
      return { connected: true, message: 'Connected' };
    } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return { 
        connected: false, 
        message: 'Server sleeping (free tier) - Real-time features unavailable' 
      };
    } else {
      return { connected: false, message: 'Connecting...' };
    }
  }

  // Admin functions for broadcasting
  broadcastStreamStart(streamData: any): void {
    if (this.socket?.connected) {
      this.socket.emit('admin-stream-start', streamData);
    }
  }

  broadcastStreamEnd(streamId: number): void {
    if (this.socket?.connected) {
      this.socket.emit('admin-stream-end', streamId);
    }
  }

  updateStreamStatus(streamId: number, status: string): void {
    if (this.socket?.connected) {
      this.socket.emit('admin-stream-status', { streamId, status });
    }
  }

  // Document collaboration methods
  joinDocument(): void {
    if (this.socket?.connected) {
      this.socket.emit('join-document');
    }
  }

  leaveDocument(): void {
    if (this.socket?.connected) {
      this.socket.emit('leave-document');
    }
  }

  sendDocumentUpdate(data: {
    content: string;
    operation: string;
    contentBefore?: string;
    positionStart?: number;
    positionEnd?: number;
    sessionId: string;
    fingerprint?: string;
  }): void {
    if (this.socket?.connected) {
      this.socket.emit('document-update', data);
    }
  }
}

export const websocketService = new WebSocketService();
export type { ChatMessage, StreamData }; 